import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { getRoutePathById } from '@bingads-webui-react/with-site-map';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { useFetch } from '@bingads-webui-campaign/react-hook-use-fetch';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { Stack } from '@fluentui/react/lib/Stack';
import {
  Title3,
} from '@fluentui/react-components';
import { fetchExperiment, parseMeasurementTypeEnum } from './fetch/experiment';
import { fetchCampaignsByIdEDM } from './fetch/campaigns';
import { fetchUetTags, getFetchedTagCollections } from './fetch/uet';
import {
  getAudienceResults,
  getBottomPerformerResults,
  getCampaignResults,
  getOverallResults,
  getTopPerformerResults,
  transformResults,
  getCampaignIds,
  fetchResults,
} from './fetch/result';
import { AudienceOverviewSection } from './results/audience-overview-section';
import { CampaignOverviewSection } from './results/campaign-overview-section';
import { ExperimentOverviewSection } from './results/experiment-overview-section';
import { ResultsPageHeader } from './results/page-header';
import { StudySummaryCard } from './results/study-summary-card';
import { downloadExcel } from './utils/download';

const mainLayoutTokens = {
  childrenGap: 15,
  padding: 30,
};

export const LiftMeasurementResultsInternal = ({ siteMap }) => {
  const { scope } = useContext(ScopeContext);
  const { fetch } = useFetch();

  const [isLoading, setIsLoading] = useState(true);
  const [experimentName, setExperimentName] = useState('');
  const [campaignsAsArray, setCampaignsAsArray] = useState([]);
  const [country, setCountry] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [measurementTypes, setMeasurementTypes] = useState({
    useConversionLift: false,
    useSearchLift: false,
    useVisitLift: false,
  });
  const [keywords, setKeywords] = useState([]);
  const [uetTags, setUetTags] = useState([]);
  const [overallResults, setOverallResults] = useState({});
  const [audienceResults, setAudienceResults] = useState({});
  const [topPerformerResults, setTopPerformerResults] = useState({});
  const [bottomPerformerResults, setBottomPerformerResults] = useState({});
  const [campaignResults, setCampaignResults] = useState({});
  const [liftStudyResults, setLiftStudyResults] = useState();

  const fetchCampaigns = useCallback(async (studyResults) => {
    try {
      const campaignIds = getCampaignIds(studyResults);
      const results = campaignIds.length > 0 ? await fetchCampaignsByIdEDM(scope.values, campaignIds) : null;

      if (!results || !results.value) return;

      const transformedCampaigns = results.value.reduce((acc, result) => {
        acc[result.Id] = { name: result.Name, id: result.Id };
        return acc;
      }, {});

      setCampaignsAsArray(Object.values(transformedCampaigns));
      setCampaignResults(getCampaignResults(studyResults, transformedCampaigns));
    } catch (e) {
      console.log('Error fetching campaigns');
      console.log(e);
    }
  }, [scope.values]);

  const i18n = useContext(I18nContext);

  const fetchLiftStudyResults = useCallback(async liftExperimentId => fetchResults({
    liftExperimentId,
    scope,
    fetch,
    onSuccess: (response) => {
      setClarityTag({
        IsFetchLiftExperimentResultsSuccessful: true,
      });
      setLiftStudyResults(response);
      return transformResults(response);
    },
    onFailure: (error) => {
      setClarityTag({
        IsFetchLiftExperimentResultsSuccessful: false,
      });
      setIsLoading(false);
      console.log('Error fetching lift study results');
      console.log(error);
    },
  }), [fetch, scope]);

  useEffect(() => {
    if (scope.values.liftExperimentId == null) {
      const route = getRoutePathById(siteMap, 'lift-measurement');
      scope.remove('liftExperimentId');
      scope.save(route);
    } else {
      setClarityTag({
        IsFetchingLiftExperimentResults: true,
        IsLiftMeasurementPage: true,
        LiftMeasurementPage: 'results',
      });
      // Fetch from the experiment, experiment results, and campaigns endpoints to build out the page state.
      fetchExperiment(scope.values, {
        onSuccess: async (liftExperiment) => {
          setExperimentName(liftExperiment.Name);
          setCountry(liftExperiment.Country);
          setStartDate(liftExperiment.StartDate);
          setEndDate(liftExperiment.EndDate);
          setMeasurementTypes(parseMeasurementTypeEnum(liftExperiment.MeasurementType));
          setKeywords(liftExperiment.Keywords == null ? [] : liftExperiment.Keywords);

          if (liftExperiment.UETTags == null) {
            setUetTags([]);
          } else {
            await fetchUetTags({
              count: liftExperiment.UETTags.length,
              ids: liftExperiment.UETTags,
              onFailure: () => {
                // Failed to fetch the UET tags so continue with the IDs.
                notificationApi.showWarning(i18n.getString(_TL_('Failed to fetch UET tags for IDs: {{ tagIds }}'), {
                  tagIds: liftExperiment.UETTags.join(i18n.getString(_TL_(', '))),
                }));
                setUetTags(liftExperiment.UETTags);
              },
              onSuccess: (data) => {
                const { fetchedTagsAsArray } = getFetchedTagCollections(data);
                const uetTagNames = fetchedTagsAsArray.map(tag => tag.name);
                setUetTags(uetTagNames);
              },
              scope,
            });
          }

          const results = await fetchLiftStudyResults(liftExperiment.Id);

          if (results) {
            await fetchCampaigns(results);
            setOverallResults(getOverallResults(results));
            setAudienceResults(getAudienceResults(results));
            setTopPerformerResults(getTopPerformerResults(results));
            setBottomPerformerResults(getBottomPerformerResults(results));
            setIsLoading(false);
          }
        },
        onFailure: (message) => {
          // We should log the details of the failure and then route the user back to the landing page.
          console.log('The fetch failed:');
          console.log(message);
        },
      });
    }
  }, [fetchCampaigns, fetchLiftStudyResults, i18n, scope, scope.values.liftExperimentId, siteMap]);

  const onClickExcel = useCallback(() => {
    if (liftStudyResults) {
      setClarityTag({
        IsDownloadingLiftMeasurementExcelResults: true,
      });
      downloadExcel(scope.values.liftExperimentId, liftStudyResults);
    }
  }, [scope.values.liftExperimentId, liftStudyResults]);

  const loadingMessage = i18n.getString(_TL_('Loading lift experiment results...'));

  return (
    <div className="lift-measurement-results">
      {isLoading ?
        <Stack tokens={mainLayoutTokens}>
          <Title3 className="loading-message">{loadingMessage}</Title3>
        </Stack>
        :
        <Stack tokens={mainLayoutTokens}>
          <ResultsPageHeader
            onClickExcel={onClickExcel}
            title={experimentName}
          />
          <StudySummaryCard
            campaigns={campaignsAsArray}
            country={country}
            endDate={endDate}
            keywords={keywords}
            startDate={startDate}
            uetTags={uetTags}
          />
          <ExperimentOverviewSection
            conversionData={overallResults.conversion}
            measurementTypes={measurementTypes}
            searchData={overallResults.search}
            visitData={overallResults.visit}
          />
          <AudienceOverviewSection
            conversionBottomPerformerData={bottomPerformerResults.conversion}
            conversionData={audienceResults.conversion}
            conversionTopPerformerData={topPerformerResults.conversion}
            measurementTypes={measurementTypes}
            searchBottomPerformerData={bottomPerformerResults.search}
            searchData={audienceResults.search}
            searchTopPerformerData={topPerformerResults.search}
            visitBottomPerformerData={bottomPerformerResults.visit}
            visitData={audienceResults.visit}
            visitTopPerformerData={topPerformerResults.visit}
          />
          <CampaignOverviewSection
            conversionData={campaignResults.conversion}
            measurementTypes={measurementTypes}
            searchData={campaignResults.search}
            visitData={campaignResults.visit}
          />
        </Stack>
      }
    </div>
  );
};

LiftMeasurementResultsInternal.propTypes = {
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  serverContext: PropTypes.shape({
    Token: PropTypes.string.isRequired,
  }).isRequired,
};

export const LiftMeasurementResults = withServerContext(LiftMeasurementResultsInternal);
